import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Divider,
  Fab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FormItemInput from "../../../shared/components/form/FormItemInput";
import { FlexContainer } from "../../../shared/components/styled/Container.styled";
import { ColouredFab } from "../../../shared/components/styled/Button.styled";
import { useTheme } from "@mui/styles";
import { ConfiguratorContext } from "../../../contexts/ConfiguratorContext";
import { stateList } from "../../../shared/constants/States.constants";
import { NON_NEGATIVE_AMOUNT_ERROR_MESSAGE } from "../../../shared/constants/Defaults.constants";

const normaliseStateFeeConfigValue = (value = "") => {
  if (!value) {
    return "";
  }

  return value
    .split(";")
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => {
      const [stateRaw, amountRaw = ""] = item.split("=");
      const state = (stateRaw || "").trim().toUpperCase();
      const amount = +amountRaw;
      if (!state || Number.isNaN(amount)) {
        return null;
      }
      return `${state}=${amount}`;
    })
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(";");
};

const serialiseDefinedStateFeeValues = (definedElementsInputValues) => {
  return Object.entries(definedElementsInputValues)
    .filter(([code, model]) => {
      if (!code || code === "undefined") {
        return false;
      }
      return !model?.errorMessage;
    })
    .map(([code, model]) => {
      const amount = +model?.value;
      if (Number.isNaN(amount)) {
        return null;
      }
      return `${code.toUpperCase()}=${amount}`;
    })
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(";");
};

const getValidationErrorMessage = (target) => {
  if (+target.value < 0) {
    return NON_NEGATIVE_AMOUNT_ERROR_MESSAGE;
  }
  if (target?.validity?.valid === false) {
    return target.validationMessage;
  }
  return "";
};

const AutoCompleteInputSetting = ({
  elementKey = "",
  elementPadding = "0",
  label = "",
  labelColor = "text",
  labelFontSize = "14px",
  padding = "0",
  background = "transparent",
  listElements = [],
  setListElements = () => {},
  onChangeHandler = () => {},
  validateInputValue = () => {},
}) => {
  const theme = useTheme();
  const { edittedFields, addNewEdittedField } = useContext(ConfiguratorContext);
  const hasInitialSyncCompletedRef = useRef(false);
  const initialConfiguratorValueRef = useRef("");

  const [selectedElement, setSelectedElement] = useState(null);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [definedElements, setDefinedElements] = useState([]);
  const [definedElementsInputValues, setDefinedElementsInputValues] = useState(
    {},
  );

  // Typing a state name only fills the text box; the picker reports a selection when
  // an option is clicked. Without this the row can read "Colorado" beside an amount
  // while the add button stays disabled, which looks like the control is broken.
  const resolvedElement =
    selectedElement ??
    listElements.find(
      (item) =>
        item?.name?.toLowerCase() === autocompleteInputValue.trim().toLowerCase(),
    ) ??
    null;

  const updateConfiguratorField = (nextDefinedElementsInputValues) => {
    const configuratorValue = serialiseDefinedStateFeeValues(
      nextDefinedElementsInputValues,
    );
    addNewEdittedField({
      key: elementKey,
      value: configuratorValue,
      modified: configuratorValue !== initialConfiguratorValueRef.current,
    });
  };

  useEffect(() => {
    const currentField = edittedFields.find((item) => item.key === elementKey);
    initialConfiguratorValueRef.current = normaliseStateFeeConfigValue(
      currentField?.value || "",
    );

    if (currentField) {
      if (currentField.value) {
        const elementsToBeDefined = [];
        const elementInputsToBeDefined = {};
        const listElementsCopy = [...listElements];
        const items = currentField.value.split(";");
        items.forEach((item) => {
          const [stateRaw, valueRaw = ""] = item.split("=");
          const state = (stateRaw || "").trim().toUpperCase();
          if (!state) {
            return;
          }
          const stateIndex = listElementsCopy.findIndex(
            (item) => item.code === state,
          );
          const amount = +valueRaw;
          if (Number.isNaN(amount)) {
            return;
          }

          elementsToBeDefined.push({
            name: stateIndex >= 0 ? listElementsCopy[stateIndex]?.name : state,
            code: state,
            value: amount,
          });
          elementInputsToBeDefined[state] = {
            value: amount,
            errorMessage: "",
          };

          if (stateIndex >= 0) {
            listElementsCopy.splice(stateIndex, 1);
          }
        });
        setDefinedElements(elementsToBeDefined);
        setDefinedElementsInputValues(elementInputsToBeDefined);
        setListElements(listElementsCopy);
      }
    }

    return () => {
      setListElements([...stateList]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasInitialSyncCompletedRef.current) {
      hasInitialSyncCompletedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definedElementsInputValues]);

  const addElementInList = () => {
    if (!resolvedElement?.code) {
      return;
    }

    const currentElement = { ...resolvedElement };
    const definedElementsCopy = [...definedElements];
    const definedElementsInputValuesCopy = { ...definedElementsInputValues };
    const listElementsCopy = [...listElements];
    const indexOfCurrentElement = listElementsCopy.findIndex(
      (item) => item.code === currentElement.code,
    );

    currentElement.value = inputValue;
    definedElementsCopy.push(currentElement);
    definedElementsInputValuesCopy[currentElement.code] = {
      value: inputValue,
      errorMessage: "",
    };

    setDefinedElements(
      definedElementsCopy.sort((a, b) => a.name.localeCompare(b.name)),
    );
    setDefinedElementsInputValues(definedElementsInputValuesCopy);
    updateConfiguratorField(definedElementsInputValuesCopy);

    if (indexOfCurrentElement >= 0) {
      listElementsCopy.splice(indexOfCurrentElement, 1);
    }
    setListElements(listElementsCopy);

    setSelectedElement(null);
    setInputValue("");
    setAutocompleteInputValue("");
  };

  const handleListInputChange = (e, code) => {
    const definedElementsInputValuesCopy = {
      ...definedElementsInputValues,
      [code]: {
        ...definedElementsInputValues[code],
        value: e.target.value,
        errorMessage: getValidationErrorMessage(e.target),
      },
    };
    setDefinedElementsInputValues(definedElementsInputValuesCopy);
    updateConfiguratorField(definedElementsInputValuesCopy);
  };

  const validateListInputValue = (e, code) => {
    const definedElementsInputValuesCopy = {
      ...definedElementsInputValues,
      [code]: {
        ...definedElementsInputValues[code],
        value: e.target.value,
        errorMessage: getValidationErrorMessage(e.target),
      },
    };
    setDefinedElementsInputValues(definedElementsInputValuesCopy);
    updateConfiguratorField(definedElementsInputValuesCopy);
  };

  const removeItemFromList = (code) => {
    const itemIndex = definedElements.findIndex((item) => item.code === code);

    const listElementsCopy = [...listElements];
    listElementsCopy.push({
      name: definedElements[itemIndex].name,
      code: definedElements[itemIndex].code,
    });
    setListElements(
      listElementsCopy.sort((a, b) => a.name.localeCompare(b.name)),
    );

    const definedElementsCopy = [...definedElements];
    definedElementsCopy.splice(itemIndex, 1);

    const definedElementsInputValuesCopy = { ...definedElementsInputValues };
    delete definedElementsInputValuesCopy[code];

    setDefinedElements(definedElementsCopy);
    setDefinedElementsInputValues(definedElementsInputValuesCopy);
    updateConfiguratorField(definedElementsInputValuesCopy);
  };

  const submitValue = (e) => {
    if (e.key === "Enter") {
      addElementInList();
    }
  };

  return (
    <FlexContainer
      flexdirection="column"
      width="100%"
      justifycontent="flex-start"
      gap="10px"
      padding={elementPadding}
    >
      <Typography
        color={labelColor}
        fontWeight="bold"
        fontSize={labelFontSize}
        sx={{ alignSelf: "flex-start" }}
      >
        {label}
      </Typography>
      <FlexContainer
        width="100%"
        flexdirection="column"
        gap="10px"
        padding={padding}
        background={background}
      >
        <FlexContainer width="100%" gap="25px" justifycontent="space-between">
          <Autocomplete
            value={selectedElement}
            onChange={(event, newValue) => {
              setSelectedElement(newValue);
            }}
            inputValue={autocompleteInputValue}
            onInputChange={(event, newInputValue) => {
              setAutocompleteInputValue(newInputValue);
            }}
            options={listElements}
            getOptionLabel={(option) => option.name}
            sx={{ maxWidth: "300px", width: "100%" }}
            renderInput={(params) => (
              <TextField variant="standard" {...params} label="Select State" />
            )}
          />
          <FlexContainer gap="25px">
            <FormItemInput
              elementWidth="200px"
              value={inputValue}
              setValue={(e) =>
                onChangeHandler(e, setInputValue, setErrorMessage)
              }
              blurHandler={(e) => validateInputValue(e, setErrorMessage)}
              blurOnWheel
              adornment
              adornmentPosition="start"
              adornmentSymbol="$"
              inputProps={{
                step: 0.01,
                min: 0,
                type: "number",
                style: {
                  textAlign: "end",
                },
              }}
              errorMessage={errorMessage}
              onKeyUp={(e) => submitValue(e)}
            />
            <Tooltip title="Add State">
              {/* A disabled button emits no pointer events, so the tooltip needs a
                  wrapper that can still receive them to explain why nothing happens. */}
              <span>
                <Fab
                  aria-label="Add State"
                  disabled={!inputValue || !resolvedElement || !!errorMessage}
                  color="primary"
                  size="small"
                  sx={{ width: 30, height: 30, minHeight: 30 }}
                  onClick={(e) => addElementInList()}
                >
                  <AddIcon />
                </Fab>
              </span>
            </Tooltip>
          </FlexContainer>
        </FlexContainer>
        {definedElements.length > 0 ? <Divider flexItem /> : ""}
        {definedElements.map((item) => (
          <FlexContainer
            width="100%"
            flexdirection="column"
            gap="10px"
            key={item.code}
          >
            <FlexContainer
              width="100%"
              gap="25px"
              justifycontent="space-between"
            >
              <Typography color="primary" fontWeight="bold" fontSize={14}>
                {item.name}
              </Typography>
              <FlexContainer gap="25px">
                <FormItemInput
                  elementWidth="200px"
                  value={definedElementsInputValues[item.code].value}
                  setValue={(e) => handleListInputChange(e, item.code)}
                  placeholder="0"
                  blurHandler={(e) => validateListInputValue(e, item.code)}
                  blurOnWheel
                  adornment
                  adornmentPosition="start"
                  adornmentSymbol="$"
                  inputProps={{
                    step: 0.01,
                    min: 0,
                    type: "number",
                    style: {
                      textAlign: "end",
                    },
                  }}
                  errorMessage={
                    definedElementsInputValues[item.code].errorMessage
                  }
                />
                <Tooltip title="Delete State">
                  <ColouredFab
                    aria-label="Delete State"
                    color={theme.palette.error.main}
                    size="small"
                    iconfontsize="1.125rem"
                    sx={{ width: 30, height: 30, minHeight: 30 }}
                    onClick={(e) => {
                      removeItemFromList(item.code);
                    }}
                  >
                    <DeleteIcon />
                  </ColouredFab>
                </Tooltip>
              </FlexContainer>
            </FlexContainer>
            <Divider flexItem />
          </FlexContainer>
        ))}
      </FlexContainer>
    </FlexContainer>
  );
};

export default AutoCompleteInputSetting;
